/**
 * Render-side number interpolator (tech-design §4, §8).
 *
 * Logic ticks at 10 tps, but the display should read smoothly at 60 fps. Rather
 * than writing the DOM/scene once per logic tick (jumpy) or doing `Decimal`
 * math every animation frame (churn), each displayed counter eases toward its
 * latest target on a plain `number` proxy. Callers convert big `Decimal`s to a
 * display number (or a `log10` proxy for sprite counts) once per tick and feed
 * that here; the render loop only touches `number`s.
 *
 * Pure and time-parameterised (`dtSeconds` is passed in, never read from a
 * clock), so it unit-tests headlessly and stays deterministic.
 */

/**
 * Exponential-smoothing interpolator toward a moving target.
 *
 * `halfLifeSeconds` is the time for the displayed value to cover half the
 * remaining distance to the target — framerate-independent, so the motion looks
 * identical at 30 or 144 fps. A `snapEpsilon` avoids asymptotic crawl: once
 * within it, the value snaps to the target.
 */
export class Interpolator {
  private displayed: number;
  private target: number;
  private readonly halfLifeSeconds: number;
  private readonly snapEpsilon: number;

  constructor(
    initial = 0,
    options: { halfLifeSeconds?: number; snapEpsilon?: number } = {},
  ) {
    this.displayed = initial;
    this.target = initial;
    this.halfLifeSeconds = options.halfLifeSeconds ?? 0.12;
    this.snapEpsilon = options.snapEpsilon ?? 1e-4;
    if (this.halfLifeSeconds <= 0) {
      throw new RangeError(
        `halfLifeSeconds must be positive, got ${String(this.halfLifeSeconds)}`,
      );
    }
  }

  /** The latest value produced by {@link advance}. */
  get value(): number {
    return this.displayed;
  }

  /** The value the display is easing toward. */
  get goal(): number {
    return this.target;
  }

  /** Point the interpolator at a new target (typically once per logic tick). */
  setTarget(target: number): void {
    this.target = target;
  }

  /** Jump the displayed value to `value` immediately (no easing). */
  snap(value: number): void {
    this.displayed = value;
    this.target = value;
  }

  /**
   * Advance the displayed value toward the target by `dtSeconds` of eased
   * motion and return the new displayed value.
   */
  advance(dtSeconds: number): number {
    if (dtSeconds <= 0) {
      return this.displayed;
    }
    const distance = this.target - this.displayed;
    if (Math.abs(distance) <= this.snapEpsilon) {
      this.displayed = this.target;
      return this.displayed;
    }
    // Fraction of the remaining distance to cover this frame. Derived from the
    // half-life so N half-lives always closes 1 - 0.5^N of the gap.
    const alpha = 1 - Math.pow(0.5, dtSeconds / this.halfLifeSeconds);
    this.displayed += distance * alpha;
    return this.displayed;
  }
}
